// PATH: src/api/notify.tsx — retours d'opération : échecs d'écriture et annulation.
//
// Deux règles de conduite, différentes selon ce qui est en jeu :
//
//   - Un ÉCHEC D'ÉCRITURE ne disparaît pas tout seul. Un toast de trois secondes
//     sur « marquer 30 élèves payés » laisse l'agent croire que c'est passé ; il
//     s'en aperçoit des jours plus tard, à la caisse. La notification reste donc
//     affichée jusqu'à ce qu'on la ferme, et propose de réessayer.
//   - Une ACTION RÉVERSIBLE s'exécute d'abord et s'annule ensuite (modèle Gmail),
//     au lieu d'exiger une confirmation avant chaque geste courant.
import React from "react";
import { Button, notification } from "antd";

/** Message d'erreur du backend, sinon un libellé selon la nature de la panne. */
const reasonOf = (err: any): string => {
  const fromServer = err?.response?.data?.error;
  if (fromServer) return String(fromServer);
  if (!err?.response) {
    return "Le serveur n'a pas répondu. Vérifiez la connexion, puis réessayez : rien n'a été enregistré.";
  }
  return "Opération refusée par le serveur.";
};

/**
 * notifyWriteError — échec d'une écriture. Persistant (duration: 0) et, si
 * l'appelant sait rejouer l'opération, muni d'un bouton « Réessayer ».
 *
 * À réserver aux ÉCRITURES : pour une lecture qui échoue, l'écran vide parle de
 * lui-même et une notification collante ne ferait qu'encombrer.
 */
export const notifyWriteError = (
  err: unknown,
  opts?: { title?: string; retry?: () => void }
): void => {
  const key = `write-error-${Date.now()}`;
  notification.error({
    key,
    message: opts?.title ?? "Enregistrement non effectué",
    description: reasonOf(err),
    duration: 0,
    btn: opts?.retry ? (
      <Button
        size="small"
        type="primary"
        onClick={() => {
          notification.destroy(key);
          opts.retry?.();
        }}
      >
        Réessayer
      </Button>
    ) : undefined,
  });
};

/**
 * runUndoable — exécute une action APRÈS un délai d'annulation. Rien n'est
 * envoyé au serveur tant que le délai court : annuler n'a donc pas besoin
 * d'opération inverse, ce qui rend le procédé applicable même aux suppressions
 * définitives.
 *
 * L'appelant est responsable de masquer l'élément immédiatement (`onOptimistic`)
 * et de le rétablir si l'on annule (`onRevert`) — sans quoi la ligne resterait
 * visible après le geste, ce qui se lit comme un bug.
 */
export const runUndoable = (opts: {
  /** Ce qui vient d'être fait, à la forme accomplie : « Devoir supprimé ». */
  label: string;
  run: () => Promise<unknown>;
  onOptimistic: () => void;
  onRevert: () => void;
  onDone?: () => void;
  delayMs?: number;
}): void => {
  const { label, run, onOptimistic, onRevert, onDone, delayMs = 6000 } = opts;
  const key = `undo-${Date.now()}`;
  let cancelled = false;

  onOptimistic();

  const timer = setTimeout(() => {
    notification.destroy(key);
    if (cancelled) return;
    run()
      .then(() => onDone?.())
      .catch((err) => {
        // L'écriture a échoué : on remet la ligne, sinon l'écran mentirait sur
        // l'état réel de la base.
        onRevert();
        notifyWriteError(err);
      });
  }, delayMs);

  notification.open({
    key,
    message: label,
    duration: delayMs / 1000,
    btn: (
      <Button
        size="small"
        onClick={() => {
          cancelled = true;
          clearTimeout(timer);
          notification.destroy(key);
          onRevert();
        }}
      >
        Annuler
      </Button>
    ),
  });
};
